import { useEffect, useMemo, useState } from 'react';
import type { DebugLogEntry, DebugLogLevel, DebugLogStats } from '../../shared/types';
import { api, debugLogQuery } from '../api';
import { formatDate } from '../utils';

const PAGE_SIZE = 100;

function toIso(value: string): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function detailsText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
}

function severityLabel(level: DebugLogLevel) {
  return ({ debug: 'Debug', info: 'Info', warn: 'Warnung', error: 'Fehler' })[level];
}

export function LogsPage({ onMessage }: {
  onMessage: (kind: 'success'|'error', message: string) => void;
}) {
  const [logs, setLogs] = useState<DebugLogEntry[]>([]);
  const [stats, setStats] = useState<DebugLogStats>({
    count: 0,
    totalBytes: 0,
    oldestAt: null,
    newestAt: null,
    levels: { debug: 0, info: 0, warn: 0, error: 0 },
  });
  const [level, setLevel] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const filter = useMemo(() => ({
    level,
    from: toIso(from),
    to: toIso(to),
    search: search.trim(),
  }), [level, from, to, search]);

  async function load(nextOffset = offset) {
    setLoading(true);
    try {
      const [list, nextStats] = await Promise.all([
        api.logs({ ...filter, limit: PAGE_SIZE, offset: nextOffset }),
        api.logStats(filter),
      ]);
      setLogs(list.logs);
      setStats(nextStats);
    } catch (error) {
      onMessage('error', error instanceof Error ? error.message : 'Der Debug-Log konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setOffset(0);
    const timer = window.setTimeout(() => void load(0), 250);
    return () => window.clearTimeout(timer);
  }, [filter.level, filter.from, filter.to, filter.search]);

  function changePage(nextOffset: number) {
    setOffset(nextOffset);
    void load(nextOffset);
  }

  function downloadCsv() {
    window.location.assign(api.logCsvUrl(filter));
  }

  async function clearAll() {
    if (!window.confirm(`Wirklich den gesamten Debug-Log mit ${stats.count} Einträgen löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }
    setClearing(true);
    try {
      const result = await api.clearLogs();
      setOffset(0);
      await load(0);
      onMessage('success', `${result.deletedCount} Logeinträge wurden vollständig gelöscht.`);
    } catch (error) {
      onMessage('error', error instanceof Error ? error.message : 'Der Debug-Log konnte nicht gelöscht werden.');
    } finally {
      setClearing(false);
    }
  }

  const first = stats.count === 0 ? 0 : offset + 1;
  const last = Math.min(offset + logs.length, stats.count);

  return <>
    <div className="page-heading">
      <div>
        <span className="eyebrow">Diagnose</span>
        <h1>Debug-Log</h1>
        <p>Fehler, Warnungen und wichtige Systemvorgänge nachvollziehen.</p>
      </div>
      <div className="heading-actions">
        <button className="button secondary" onClick={() => void load()} disabled={loading}>Aktualisieren</button>
        <button className="button secondary" onClick={downloadCsv}>CSV herunterladen</button>
        <button className="button danger-ghost" onClick={() => void clearAll()} disabled={clearing || stats.count === 0}>
          {clearing ? 'Löscht …' : 'Gesamten Log löschen'}
        </button>
      </div>
    </div>

    <section className="log-stat-grid">
      <div className="stat-card"><span>Einträge</span><strong>{stats.count}</strong></div>
      <div className="stat-card"><span>Log-Größe</span><strong>{formatBytes(stats.totalBytes)}</strong></div>
      <div className="stat-card error-card"><span>Fehler</span><strong>{stats.levels.error}</strong></div>
      <div className="stat-card warning-card"><span>Warnungen</span><strong>{stats.levels.warn}</strong></div>
    </section>

    <section className="panel log-panel">
      <div className="log-filterbar">
        <label>
          <span>Textsuche</span>
          <input placeholder="Meldung, Ereignis, Benutzer, Vorgangs-ID …" value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
        <label>
          <span>Level</span>
          <select value={level} onChange={(event) => setLevel(event.target.value)}>
            <option value="">Alle Level</option>
            <option value="debug">Debug</option>
            <option value="info">Info</option>
            <option value="warn">Warnung</option>
            <option value="error">Fehler</option>
          </select>
        </label>
        <label>
          <span>Von Datum/Uhrzeit</span>
          <input type="datetime-local" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>
          <span>Bis Datum/Uhrzeit</span>
          <input type="datetime-local" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
      </div>

      <div className="log-range">
        <span>{first}–{last} von {stats.count}</span>
        <span>
          {stats.oldestAt ? `Ältester: ${formatDate(stats.oldestAt)}` : 'Noch keine Einträge'}
          {stats.newestAt ? ` · Neuester: ${formatDate(stats.newestAt)}` : ''}
        </span>
      </div>

      <div className="table-wrap">
        <table className="log-table">
          <thead>
            <tr>
              <th>Datum/Uhrzeit</th>
              <th>Level</th>
              <th>Quelle / Ereignis</th>
              <th>Meldung</th>
              <th>Kontext</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((entry) => <tr key={entry.id}>
              <td className="log-time">{formatDate(entry.createdAt)}<small>#{entry.id} · {formatBytes(entry.estimatedBytes)}</small></td>
              <td><span className={`log-level ${entry.level}`}>{severityLabel(entry.level)}</span></td>
              <td><strong>{entry.source}</strong><small>{entry.event}</small></td>
              <td className="log-message">
                <strong>{entry.message}</strong>
                {entry.details !== null && <details>
                  <summary>Technische Details</summary>
                  <pre>{detailsText(entry.details)}</pre>
                </details>}
              </td>
              <td className="log-context">
                {entry.actorName && <span>Benutzer: {entry.actorName}</span>}
                {entry.method && entry.route && <span>{entry.method} {entry.route}</span>}
                {entry.statusCode !== null && <span>HTTP {entry.statusCode}</span>}
                {entry.durationMs !== null && <span>{entry.durationMs} ms</span>}
                {entry.requestId && <code title="Vorgangs-ID">{entry.requestId}</code>}
              </td>
            </tr>)}
          </tbody>
        </table>
      </div>

      {loading && <div className="empty">Debug-Log wird geladen …</div>}
      {!loading && logs.length === 0 && <div className="empty">
        <h3>Keine Logeinträge gefunden</h3>
        <p>Ändere den Filter oder löse den Vorgang erneut aus.</p>
      </div>}

      <div className="log-pagination">
        <button className="button secondary" disabled={offset === 0 || loading} onClick={() => changePage(Math.max(0, offset - PAGE_SIZE))}>← Neuere</button>
        <button className="button secondary" disabled={offset + PAGE_SIZE >= stats.count || loading} onClick={() => changePage(offset + PAGE_SIZE)}>Ältere →</button>
      </div>
    </section>
  </>;
}
